// The shop's screen on @rpg/ui (decision 0046: Preact screens on the screen stack, keyboard, pad and touch focus,
// semantic HTML with ARIA names, every text from the game's strings by id): the wallet, the standing discount, and two
// tabs, buy (the stock, each line's price and what's left) and sell (what the shop buys from the bag, each at its
// price). Each press trades through the feature at once and says why when refused. Cut from spike 002's
// src/rpg/screens.ts; the element tree is the same, so the game's specs and shots read it as before.
import { Button, h, List, ListRow, Screen, Status, TabPanel, Tabs, type ScreenContext, type ScreenRender, type VNode } from "@rpg/ui";
import type { TradeRefusal } from "@rpg/items";
import type { ShopFeature } from "./index";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type El = VNode<any>;

/** The string ids the screen says things with (defaults in `SHOP_STRINGS`; a game passes its own). */
export interface ShopLabels {
  /** `{amount}`: the wallet. */
  gold: string;
  /** `{percent}`: the discount now. */
  discount: string;
  buyTab: string;
  sellTab: string;
  /** `{count}`: a limited line's stock left. */
  stockLeft: string;
  /** `{item}`, `{price}`. */
  buy: string;
  sell: string;
  /** `{item}`, `{count}`. */
  itemLine: string;
  equipped: string;
  nothing: string;
  /** A refused trade's reason, by its code. */
  refused(code: TradeRefusal): string;
}

/** The unit's default labels (its own string ids). */
export const SHOP_LABELS: ShopLabels = {
  gold: "shop.gold",
  discount: "shop.discount",
  buyTab: "shop.buyTab",
  sellTab: "shop.sellTab",
  stockLeft: "shop.stockLeft",
  buy: "shop.buy",
  sell: "shop.sell",
  itemLine: "shop.itemLine",
  equipped: "shop.equipped",
  nothing: "shop.nothing",
  refused: (code) => `shop.refuse.${code}`,
};

/** The screen's params: which shop, and whether it was opened from the world (back resumes play). */
export interface ShopScreenParams {
  id: string;
  fromWorld?: boolean;
}

/** What the screen reads and says things with. */
export interface ShopScreenHost {
  /** The shop feature (null before the scene has one: the screen draws empty). */
  feature(): ShopFeature | null;
  /** A string by id, with values. */
  t(id: string, vars?: Record<string, string | number>): string;
  itemName(item: string): string;
  /** An item's one-line description (a string id), or null. */
  itemInfo(item: string): string | null;
  /** An item's icon, or null. */
  icon(item: string): El | null;
  count(item: string): number;
  equipped(item: string): boolean;
  /** Back's action when the screen was opened from the world (resume play); undefined: the stack's own back. */
  back?(ctx: ScreenContext): (() => void) | undefined;
  /** The game's string ids (default the unit's). */
  labels?: ShopLabels;
}

/** The shop screen, for `addScreens({ shop: shopScreen(host) })`; push it with `{ id, fromWorld }`. */
export function shopScreen(d: ShopScreenHost): ScreenRender {
  const L = d.labels ?? SHOP_LABELS;
  const t = d.t;
  // each open screen's last result and selected tab (by its stack key)
  const status = new Map<number, string>();
  const tabs = new Map<number, string>();
  const act = (ctx: ScreenContext, result: string | null): void => {
    status.set(ctx.key, result ?? "");
    ctx.ui.refresh();
  };
  return (ctx) => {
    const r = d.feature();
    if (!r) return h("div", null);
    const p = ctx.params as ShopScreenParams;
    const info = r.get(p.id);
    if (!info) throw new Error(`shop screen: no shop "${p.id}"`);
    const s = r.trader();
    const sel = tabs.get(ctx.key) ?? "buy";
    const off = r.discount(p.id);
    const back = d.back?.(ctx);
    const sellable = s.inv.stacks.filter((x, i, all) => all.findIndex((y) => y.item === x.item) === i && r.shops.buys(p.id, x.item));
    const why = (x: { refused: TradeRefusal } | object): string | null => ("refused" in x ? t(L.refused(x.refused)) : null);
    return h(
      Screen,
      { ctx, title: t(info.name), dim: true, ...(back ? { onBack: back } : {}) },
      h("p", { class: "rpg-hint", "data-gold": String(s.wallet.balance) }, t(L.gold, { amount: s.wallet.balance })),
      off > 0 ? h("p", { class: "rpg-hint", "data-discount": String(off) }, t(L.discount, { percent: Math.round(off * 100) })) : null,
      h(Tabs, { id: "shop", label: t(info.name), tabs: [{ id: "buy", label: t(L.buyTab) }, { id: "sell", label: t(L.sellTab) }], selected: sel, onSelect: (id: string) => (tabs.set(ctx.key, id), ctx.ui.refresh()) }),
      h(
        TabPanel,
        { id: "shop", selected: sel },
        sel === "buy"
          ? h(
              List,
              { id: "stock", label: t(L.buyTab) },
              info.def.stock.map((l) => {
                const left = r.shops.stockLeft(s.state, p.id, l.item);
                const price = r.shops.buyPrice(p.id, l.item, off);
                const detail = d.itemInfo(l.item);
                return h(
                  ListRow,
                  { id: `buy-${l.item}`, title: d.itemName(l.item), ...(detail ? { detail: t(detail) } : {}), ...(Number.isFinite(left) ? { badge: left > 0 ? t(L.stockLeft, { count: left }) : t(L.refused("stock")) } : {}), icon: d.icon(l.item) },
                  h(Button, { label: t(L.buy, { item: d.itemName(l.item), price }), action: `buy-${l.item}`, disabled: left <= 0, onPress: () => act(ctx, why(r.buy(p.id, l.item, 1))) }),
                );
              }),
            )
          : sellable.length
            ? h(
                List,
                { id: "sell", label: t(L.sellTab) },
                sellable.map((x) =>
                  h(
                    ListRow,
                    { id: `sell-${x.item}`, title: t(L.itemLine, { item: d.itemName(x.item), count: d.count(x.item) }), ...(d.equipped(x.item) ? { badge: t(L.equipped) } : {}), icon: d.icon(x.item) },
                    h(Button, { label: t(L.sell, { item: d.itemName(x.item), price: r.shops.sellPrice(p.id, x.item, off) }), action: `sell-${x.item}`, onPress: () => act(ctx, why(r.sell(p.id, x.item, 1))) }),
                  ),
                ),
              )
            : h("p", { class: "rpg-hint" }, t(L.nothing)),
      ),
      h(Status, { text: status.get(ctx.key) ?? "" }),
    );
  };
}
